import React from 'react';
import { motion } from 'framer-motion';
import { UserIcon, ArrowPathIcon, BoltIcon } from '@heroicons/react/24/outline';

const FundingSection = () => {
  const fundingMethods = [
    {
      icon: UserIcon,
      title: "Crypto-friendly bank transfers",
      description: "Your personal USD, EUR, and GBP accounts on Segway help you prevent blocked or delayed transactions that banks can impose on third-party transfers."
    },
    {
      icon: ArrowPathIcon,
      title: "Seamless crypto transfers",
      description: "Receive crypto from external platforms or wallets across 20+ blockchain networks like Ethereum, Solana, and Tron."
    },
    {
      icon: BoltIcon,
      title: "Instant and local payment solutions",
      description: "Choose between instant card payments with Visa, Mastercard, Apple Pay, Google Pay or popular regional payment methods that work best for you."
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-16">
          {/* Left Content - Title */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.2 
            }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Fund your account
              <br />
              effortlessly.
            </motion.h2>
          </motion.div>

          {/* Right Content - Description */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.4 
            }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="text-lg text-gray-600 leading-relaxed"
            >
              Add funds via bank transfers under your name, or choose from simple alternatives that match your needs.
            </motion.p>
          </motion.div>
        </div>

        <div className="space-y-8">
          {fundingMethods.map((method, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                ease: [0.25, 0.46, 0.45, 0.94],
                delay: index * 0.2 
              }}
              viewport={{ once: true, margin: "-50px" }}
              className="group"
            >
              <div className="flex items-start space-x-6">
                {/* Icon */}
                <motion.div 
                  className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-all duration-300"
                  whileHover={{ 
                    scale: 1.1,
                    rotate: 5,
                    transition: { duration: 0.2 }
                  }}
                >
                  <method.icon className="w-6 h-6 text-gray-700" />
                </motion.div>

                {/* Content */}
                <div className="flex-1">
                  <motion.h3 
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 + 0.4 }}
                    viewport={{ once: true }}
                    className="text-lg lg:text-xl font-bold text-gray-900 mb-3"
                  >
                    {method.title}
                  </motion.h3>
                  <motion.p 
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 + 0.6 }}
                    viewport={{ once: true }}
                    className="text-gray-600 text-base leading-relaxed"
                  >
                    {method.description}
                  </motion.p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FundingSection; 